/**
 * Trend Indicators Page
 * Story: E007-S014 - Trend Indicators (↑↓→)
 * 
 * Accessible at: /trends
 */

import React, { useState } from 'react';
import { TrendIndicatorEnhanced } from '../components/TrendIndicatorEnhanced';
import { TrendControls, TrendPeriodSelector, TrendSortSelect } from '../components/TrendControls';
import { TrendData, TrendSortOption } from '../lib/trendsApi';

// Mock data for demonstration
const mockCampaigns = [
  {
    id: '1',
    name: 'Summer Sale 2026',
    platform: 'Meta',
    status: 'active',
    spend: 12500,
    roas: {
      direction: 'up' as const,
      change_percent: 15.6,
      previous_value: 2.45,
      current_value: 2.83,
      daily_values: [2.45, 2.52, 2.60, 2.68, 2.75, 2.80, 2.83],
      is_alert: false,
    },
    ctr: {
      direction: 'up' as const,
      change_percent: 8.2,
      previous_value: 1.8,
      current_value: 1.95,
      daily_values: [1.8, 1.82, 1.85, 1.88, 1.90, 1.93, 1.95],
      is_alert: false,
    },
    cpa: {
      direction: 'down' as const,
      change_percent: -12.3,
      previous_value: 45.20,
      current_value: 39.64,
      daily_values: [45.2, 44.1, 42.8, 41.5, 40.9, 40.2, 39.64],
      is_alert: false,
    },
  },
  {
    id: '2',
    name: 'Brand Awareness Q1',
    platform: 'Google',
    status: 'active',
    spend: 8750,
    roas: {
      direction: 'down' as const,
      change_percent: -25.4,
      previous_value: 3.20,
      current_value: 2.39,
      daily_values: [3.20, 3.05, 2.88, 2.70, 2.55, 2.45, 2.39],
      is_alert: true,
    },
    ctr: {
      direction: 'stable' as const,
      change_percent: 1.2,
      previous_value: 2.1,
      current_value: 2.125,
      daily_values: [2.1, 2.1, 2.12, 2.11, 2.12, 2.12, 2.125],
      is_alert: false,
    },
    cpa: {
      direction: 'up' as const,
      change_percent: 18.7,
      previous_value: 32.50,
      current_value: 38.58,
      daily_values: [32.5, 33.8, 35.2, 36.4, 37.2, 38.0, 38.58],
      is_alert: false,
    },
  },
  {
    id: '3',
    name: 'Product Launch TikTok',
    platform: 'TikTok',
    status: 'active',
    spend: 5200,
    roas: {
      direction: 'up' as const,
      change_percent: 42.1,
      previous_value: 1.90,
      current_value: 2.70,
      daily_values: [1.9, 2.1, 2.25, 2.40, 2.52, 2.62, 2.70],
      is_alert: false,
    },
    ctr: {
      direction: 'up' as const,
      change_percent: 22.5,
      previous_value: 3.2,
      current_value: 3.92,
      daily_values: [3.2, 3.35, 3.50, 3.65, 3.78, 3.85, 3.92],
      is_alert: false,
    },
    cpa: {
      direction: 'down' as const,
      change_percent: -28.9,
      previous_value: 28.40,
      current_value: 20.19,
      daily_values: [28.4, 26.5, 24.8, 23.2, 22.1, 21.0, 20.19],
      is_alert: false,
    },
  },
];

export const TrendIndicatorsPage: React.FC = () => {
  const [period, setPeriod] = useState('7d');
  const [sort, setSort] = useState<TrendSortOption>(null);
  const [showSparklines, setShowSparklines] = useState(true);

  // Count alerts
  const alertCount = mockCampaigns.filter(c => c.roas.is_alert).length;

  return (
    <div className="p-4 md:p-6 2xl:p-10">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-black dark:text-white mb-2">
          Trend Indicators
        </h1>
        <p className="text-bodydark dark:text-bodydark1">
          Monitor campaign performance trends with visual indicators. Hover over any indicator to see detailed comparison.
        </p>
      </div>

      {/* Controls Bar */}
      <div className="bg-white dark:bg-boxdark rounded-lg p-4 mb-6 shadow-sm">
        <TrendControls
          period={period}
          onPeriodChange={setPeriod}
          sort={sort}
          onSortChange={setSort}
          showAlertCount={alertCount}
        />
        
        {/* Toggle sparklines */}
        <div className="mt-4 pt-4 border-t border-stroke dark:border-strokedark">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={showSparklines}
              onChange={(e) => setShowSparklines(e.target.checked)}
              className="rounded border-stroke dark:border-strokedark"
            />
            <span className="text-sm text-bodydark dark:text-bodydark1">
              Show inline sparkline charts
            </span>
          </label>
        </div>
      </div>

      {/* Component Showcase */}
      <div className="bg-white dark:bg-boxdark rounded-lg p-6 mb-6 shadow-sm">
        <h2 className="text-lg font-semibold text-black dark:text-white mb-4">
          Component Showcase
        </h2>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Positive Trend */}
          <div className="p-4 bg-gray-50 dark:bg-meta-4 rounded-lg">
            <h3 className="text-sm font-medium text-bodydark dark:text-bodydark1 mb-2">
              Positive Trend (ROAS ↑)
            </h3>
            <TrendIndicatorEnhanced
              trend={{
                direction: 'up',
                change_percent: 15.6,
                previous_value: 2.45,
                current_value: 2.83,
                daily_values: [2.45, 2.52, 2.60, 2.68, 2.75, 2.80, 2.83],
              }}
              metricLabel="ROAS"
              metricFormat="multiplier"
              showSparkline={showSparklines}
            />
          </div>

          {/* Negative Trend with Alert */}
          <div className="p-4 bg-gray-50 dark:bg-meta-4 rounded-lg">
            <h3 className="text-sm font-medium text-bodydark dark:text-bodydark1 mb-2">
              Declining Trend with Alert (more than 20%)
            </h3>
            <TrendIndicatorEnhanced
              trend={{
                direction: 'down',
                change_percent: -25.4,
                previous_value: 3.20,
                current_value: 2.39,
                daily_values: [3.20, 3.05, 2.88, 2.70, 2.55, 2.45, 2.39],
                is_alert: true,
              }}
              metricLabel="ROAS"
              metricFormat="multiplier"
              showSparkline={showSparklines}
            />
          </div>

          {/* Stable Trend */}
          <div className="p-4 bg-gray-50 dark:bg-meta-4 rounded-lg">
            <h3 className="text-sm font-medium text-bodydark dark:text-bodydark1 mb-2">
              Stable Trend (CTR)
            </h3>
            <TrendIndicatorEnhanced
              trend={{
                direction: 'stable',
                change_percent: 1.2,
                previous_value: 2.1,
                current_value: 2.125,
                daily_values: [2.1, 2.1, 2.12, 2.11, 2.12, 2.12, 2.125],
              }}
              metricLabel="CTR"
              metricFormat="percentage"
              showSparkline={showSparklines}
            />
          </div>
        </div>
      </div>

      {/* Campaign Table with Trends */}
      <div className="bg-white dark:bg-boxdark rounded-lg shadow-sm overflow-hidden">
        <div className="p-4 border-b border-stroke dark:border-strokedark">
          <h2 className="text-lg font-semibold text-black dark:text-white">
            Campaign Performance with Trends
          </h2>
        </div>
        
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-gray-50 dark:bg-meta-4">
                <th className="px-4 py-3 text-left text-sm font-medium text-bodydark dark:text-bodydark1">
                  Campaign
                </th>
                <th className="px-4 py-3 text-left text-sm font-medium text-bodydark dark:text-bodydark1">
                  Platform
                </th>
                <th className="px-4 py-3 text-right text-sm font-medium text-bodydark dark:text-bodydark1">
                  Spend
                </th>
                <th className="px-4 py-3 text-center text-sm font-medium text-bodydark dark:text-bodydark1">
                  ROAS Trend
                </th>
                <th className="px-4 py-3 text-center text-sm font-medium text-bodydark dark:text-bodydark1">
                  CTR Trend
                </th>
                <th className="px-4 py-3 text-center text-sm font-medium text-bodydark dark:text-bodydark1">
                  CPA Trend
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-stroke dark:divide-strokedark">
              {mockCampaigns.map((campaign) => (
                <tr key={campaign.id} className="hover:bg-gray-50 dark:hover:bg-meta-4 transition-colors">
                  <td className="px-4 py-3">
                    <div className="font-medium text-black dark:text-white">
                      {campaign.name}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-1 rounded text-xs font-medium ${
                      campaign.platform === 'Meta' ? 'bg-blue-100 text-blue-700' :
                      campaign.platform === 'Google' ? 'bg-green-100 text-green-700' :
                      'bg-pink-100 text-pink-700'
                    }`}>
                      {campaign.platform}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right font-medium text-black dark:text-white">
                    ${campaign.spend.toLocaleString()}
                  </td>
                  <td className="px-4 py-3 text-center">
                    <TrendIndicatorEnhanced
                      trend={campaign.roas}
                      metricLabel="ROAS"
                      metricFormat="multiplier"
                      showSparkline={showSparklines}
                    />
                  </td>
                  <td className="px-4 py-3 text-center">
                    <TrendIndicatorEnhanced
                      trend={campaign.ctr}
                      metricLabel="CTR"
                      metricFormat="percentage"
                      showSparkline={showSparklines}
                    />
                  </td>
                  <td className="px-4 py-3 text-center">
                    <TrendIndicatorEnhanced
                      trend={campaign.cpa}
                      metricLabel="CPA"
                      metricFormat="currency"
                      showSparkline={showSparklines}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Size Variations */}
      <div className="bg-white dark:bg-boxdark rounded-lg p-6 mt-6 shadow-sm">
        <h2 className="text-lg font-semibold text-black dark:text-white mb-4">
          Size Variations
        </h2>
        <div className="flex items-center gap-6 flex-wrap">
          <div>
            <span className="text-xs text-bodydark dark:text-bodydark1 block mb-1">Small</span>
            <TrendIndicatorEnhanced
              trend={{ direction: 'up', change_percent: 12.5, previous_value: 100, current_value: 112.5 }}
              metricLabel="Value"
              size="sm"
              showSparkline={false}
            />
          </div>
          <div>
            <span className="text-xs text-bodydark dark:text-bodydark1 block mb-1">Medium (default)</span>
            <TrendIndicatorEnhanced
              trend={{ direction: 'up', change_percent: 12.5, previous_value: 100, current_value: 112.5 }}
              metricLabel="Value"
              size="md"
              showSparkline={false}
            />
          </div>
          <div>
            <span className="text-xs text-bodydark dark:text-bodydark1 block mb-1">Large</span>
            <TrendIndicatorEnhanced
              trend={{ direction: 'up', change_percent: 12.5, previous_value: 100, current_value: 112.5 }}
              metricLabel="Value"
              size="lg"
              showSparkline={false}
            />
          </div>
        </div>
      </div>

      {/* Usage Guide */}
      <div className="bg-white dark:bg-boxdark rounded-lg p-6 mt-6 shadow-sm">
        <h2 className="text-lg font-semibold text-black dark:text-white mb-4">
          How to Use
        </h2>
        <div className="prose dark:prose-invert max-w-none">
          <ul className="text-sm text-bodydark dark:text-bodydark1 space-y-2">
            <li><strong>↑ Green arrow:</strong> Positive change (improving performance)</li>
            <li><strong>↓ Red arrow:</strong> Negative change (declining performance)</li>
            <li><strong>→ Gray arrow:</strong> Stable (±5% change)</li>
            <li><strong>⚠️ Alert icon:</strong> Sharp decline detected (more than 20% drop)</li>
            <li><strong>Hover for details:</strong> See previous/current values and sparkline chart</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default TrendIndicatorsPage;
